import React, { useState } from 'react';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import { Spinner, Scales, Copy, Check, Trash, FileText } from '@phosphor-icons/react';

const CATEGORY_LABELS = {
  correction: 'Correction',
  apology: 'Apology',
  policy_change: 'Policy change',
  compensation: 'Compensation',
  disciplinary: 'Disciplinary',
  other: 'Other',
};

const RemedyLetterCard = ({ letter, onDelete }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${letter.subject}\n\n${letter.body}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      logger.error('clipboard copy failed:', err);
    }
  };
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={`remedy-letter-${letter.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-sm font-ibm font-semibold text-[#09090B]">{letter.subject}</p>
          <p className="text-xs text-[#6B7280] font-ibm">To: {letter.recipient}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={copy} data-testid={`remedy-letter-copy-${letter.id}`}
            className="flex items-center gap-1 text-xs font-ibm text-[#6B7280] hover:text-[#002FA7]">
            {copied ? <><Check size={12} className="text-green-600" /> Copied</> : <><Copy size={12} /> Copy</>}
          </button>
          <button onClick={() => onDelete(letter.id)} data-testid={`remedy-letter-delete-${letter.id}`}
            className="flex items-center gap-1 text-xs font-ibm text-[#6B7280] hover:text-red-600"><Trash size={12} /> Delete</button>
        </div>
      </div>
      {(letter.remedies || []).length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          {letter.remedies.map((r, i) => (
            <span key={`${letter.id}-r-${i}`} className="text-[10px] font-ibm px-1.5 py-0.5 rounded bg-[#F3F4F6] text-[#6B7280]">{r}</span>
          ))}
        </div>
      )}
      <pre className="text-xs text-[#09090B] font-ibm whitespace-pre-wrap leading-relaxed bg-[#F9FAFB] border border-[#F3F4F6] rounded-md p-3">{letter.body}</pre>
    </div>
  );
};

export const RemedyBuilderSection = ({ caseId, plan, onPlanChange }) => {
  const remedies = plan.suggested_remedies || [];
  const [selected, setSelected] = useState(() => remedies.map((_, i) => i));
  const [recipient, setRecipient] = useState('');
  const [drafting, setDrafting] = useState(false);

  const toggle = (i) => setSelected(prev => (prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]));

  const draft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/escalation/remedy-letter`, {
        remedy_indices: selected, recipient: recipient.trim(),
      });
      onPlanChange(prev => ({ ...prev, remedy_letters: [data.letter, ...(prev.remedy_letters || [])] }));
    } catch (err) {
      logger.error('failed to draft remedy letter:', err);
      alert('Failed to draft the remedy demand letter.');
    } finally {
      setDrafting(false);
    }
  };

  const remove = async (letterId) => {
    const prev = plan.remedy_letters || [];
    onPlanChange(p => ({ ...p, remedy_letters: (p.remedy_letters || []).filter(l => l.id !== letterId) }));
    try {
      await api.delete(`/cases/${caseId}/escalation/remedy-letters/${letterId}`);
    } catch (err) {
      logger.error('failed to delete remedy letter:', err);
      onPlanChange(p => ({ ...p, remedy_letters: prev }));
    }
  };

  return (
    <div className="border-t border-[#E5E7EB] pt-5 space-y-4" data-testid="remedy-builder">
      <div>
        <h4 className="font-outfit text-base font-semibold text-[#09090B] flex items-center gap-2">
          <Scales size={18} className="text-[#002FA7]" weight="fill" /> Remedy Builder
        </h4>
        <p className="text-sm text-[#6B7280] font-ibm mt-0.5">Pick the remedies your evidence supports, then draft a formal remedy demand letter.</p>
      </div>

      {remedies.length === 0 ? (
        <p className="text-sm text-[#6B7280] font-ibm italic">No suggested remedies — rebuild the escalation plan to generate them.</p>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-2" data-testid="remedy-list">
          {remedies.map((r, i) => (
            <label key={`remedy-${i}`} className="flex items-start gap-2.5 cursor-pointer group" data-testid={`remedy-item-${i}`}>
              <input type="checkbox" checked={selected.includes(i)} onChange={() => toggle(i)}
                data-testid={`remedy-checkbox-${i}`}
                className="mt-0.5 accent-[#002FA7] w-4 h-4 shrink-0" />
              <div className="flex-1">
                <span className="text-sm font-ibm font-semibold text-[#09090B]">{r.title}</span>
                <span className="text-[10px] font-ibm font-semibold px-1.5 py-0.5 rounded bg-[#002FA7]/10 text-[#002FA7] ml-2">{CATEGORY_LABELS[r.category] || r.category}</span>
                {r.rationale && <p className="text-xs text-[#6B7280] font-ibm mt-0.5">{r.rationale}</p>}
              </div>
            </label>
          ))}
        </div>
      )}

      {remedies.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <input value={recipient} onChange={e => setRecipient(e.target.value)}
            placeholder="Recipient (optional — e.g. School Principal)"
            data-testid="remedy-recipient-input"
            className="flex-1 min-w-[220px] text-sm font-ibm border border-[#E5E7EB] rounded-md px-3 py-2 focus:outline-none focus:border-[#002FA7]" />
          <button onClick={draft} disabled={drafting || selected.length === 0} data-testid="remedy-draft-button"
            className="flex items-center gap-1.5 px-4 py-2 bg-[#002FA7] text-white font-ibm font-semibold rounded-md text-sm hover:bg-[#00237D] disabled:opacity-50 transition-all">
            {drafting ? <><Spinner size={14} className="animate-spin" /> Drafting…</> : <><FileText size={14} weight="fill" /> Draft Remedy Demand ({selected.length})</>}
          </button>
        </div>
      )}

      {(plan.remedy_letters || []).length > 0 && (
        <div className="space-y-3" data-testid="remedy-letters">
          {plan.remedy_letters.map(l => <RemedyLetterCard key={l.id} letter={l} onDelete={remove} />)}
        </div>
      )}
    </div>
  );
};
